import { ChevronDown, ChevronUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { tournamentStatusVariant } from '../lib/badgeVariants'
import { champsFromLeaderboard } from '../lib/groupTournaments'
import { fetchLeaderboardRankings } from '../services/tournamentService'
import DubChampBadge from './DubChampBadge'
import TournamentDetailPanel from './TournamentDetailPanel'
import { Badge } from './ui/badge'
import { Button } from './ui/button'

const TIMING_LABELS = { live: 'Live', upcoming: 'Upcoming', past: 'Past' }

// One tournament on the group page, built like a "My Tournaments" card on
// Home: a header that expands into Brackets / Leaderboard / Prizes, scoped to
// the group. A finished tournament names its dub Champ in the header.
export default function GroupTournamentCard({ tournament, groupId, defaultExpanded = false, isOwner = false, onRemove }) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [champs, setChamps] = useState([])

  useEffect(() => {
    if (tournament.timing !== 'past') return undefined
    let cancelled = false
    fetchLeaderboardRankings({ groupId, tournamentId: tournament.id, limit: 5 })
      .then(rows => { if (!cancelled) setChamps(champsFromLeaderboard(rows)) })
      .catch(err => console.error('Error loading group champ:', err))
    return () => { cancelled = true }
  }, [groupId, tournament.id, tournament.timing])

  const champNames = champs.map(c => c.full_name).filter(Boolean)

  return (
    <div className="card-glass-container">
      <div className="flex items-start gap-2 p-4">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          aria-expanded={expanded}
          className="flex-1 min-w-0 flex items-start justify-between gap-3 text-left focus:outline-none focus-visible:outline-none"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="text-lg font-semibold break-words">{tournament.name}</h3>
              <Badge variant={tournamentStatusVariant[tournament.timing] || 'muted'}>
                {TIMING_LABELS[tournament.timing]}
              </Badge>
            </div>
            {champNames.length > 0 ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                <DubChampBadge />
                <span className="font-medium text-foreground">{champNames.join(' & ')}</span>
              </p>
            ) : !expanded && (
              <p className="text-sm text-muted-foreground">View brackets, leaderboard and prizes</p>
            )}
          </div>
          <span className="flex-shrink-0 pt-1">
            {expanded
              ? <ChevronUp className="h-5 w-5 text-muted-foreground" />
              : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
          </span>
        </button>
      </div>

      {expanded && (
        <div className="px-2 pb-2 sm:p-4 sm:pt-0">
          <TournamentDetailPanel
            tournamentId={tournament.id}
            groupId={groupId}
            groupBracketIds={tournament.bracketIds}
            champUserIds={champs.map(c => c.user_id)}
            champNames={champNames}
            showHero={false}
            swipeable={false}
          />
          {isOwner && onRemove && (
            <div className="mt-3 text-right">
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => onRemove(tournament)}>
                Remove tournament from group
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
